import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import QRCode from 'qrcode';
import { useParams, useLocation } from 'react-router-dom';
import { reportingApi, devFilesApi, API_BASE_URL } from '../../services/api';
import { getReportMeta } from './config/reportRegistry';
import { useNotifications } from '../../context/NotificationContext';
import { useDataTransfer } from '../../context/DataTransferContext';
import ReportParameterTable from './components/ReportParameterTable';
import ReportPaginator from './components/ReportPaginator';
import { FileSpreadsheet, GripVertical, Ban, ChevronDown, ChevronRight, Play, FilterX, Download } from 'lucide-react';
import wmLogo from '../../assets/logos/WM-Logo-Full.png';
import wmLogoCb from '../../assets/WM-Logo-CB-Transparent-White5.png';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import exportReportPdf from './export/reportPdfExporter';
import exportReportExcel from './export/reportExcelExporter';
import exportReportCsv from './export/reportCsvExporter';
import buildReportFilename from './export/reportFilename';

// Mirrors backend ToCamelCase: all-uppercase names (e.g. "DTC") go fully lowercase,
// others just lower the first character.
const toCamelCase = (name) => {
  if (!name) return name;
  if ([...name].every(c => (c >= 'A' && c <= 'Z') || c === '_')) return name.toLowerCase();
  return name.charAt(0).toLowerCase() + name.slice(1);
};

// Canvas-based text measurement for fixed header column sizing
const _mc = document.createElement('canvas').getContext('2d');
const measureTextPx = (text, font) => { _mc.font = font; return _mc.measureText(String(text)).width; };

// Split a multi-word column header into at most 2 display lines.
// 1 word → [label, ''] | 2 words → [word1, word2] | 3+ → optimal split minimizing max line length
const splitHeaderLabel = (label) => {
  const words = label.split(' ');
  if (words.length <= 1) return [label, ''];
  if (words.length === 2) return [words[0], words[1]];
  let bestSplit = 1, bestMax = Infinity;
  for (let i = 1; i < words.length; i++) {
    const m = Math.max(words.slice(0, i).join(' ').length, words.slice(i).join(' ').length);
    if (m < bestMax) { bestMax = m; bestSplit = i; }
  }
  return [words.slice(0, bestSplit).join(' '), words.slice(bestSplit).join(' ')];
};

// Reports that always group by a fixed field — grouping selector is locked for these.
const FIXED_GROUP_BY = {
  'slugRptStd-Sub-Index_Report_Panels': 'Index_Sheet',
  'slugRptStd-Sub-Index_Report_Tags':   'Index_Sheet',
};

// Columns hidden from preview/PDF for Index reports (group field + spec-label columns)
const INDEX_REPORT_HIDDEN_FIELDS = new Set([
  'Index_Sheet', 'IDX_SPEC_1_COL', 'IDX_SPEC_2_COL',
  'IDX_SPEC_3_COL', 'IDX_SPEC_4_COL', 'IDX_SPEC_5_COL',
]);
// Maps each spec-label field to the spec data field whose header it drives
const INDEX_SPEC_COL_MAP = [
  ['IDX_SPEC_1_COL', 'IDX_SPEC_1'],
  ['IDX_SPEC_2_COL', 'IDX_SPEC_2'],
  ['IDX_SPEC_3_COL', 'IDX_SPEC_3'],
  ['IDX_SPEC_4_COL', 'IDX_SPEC_4'],
  ['IDX_SPEC_5_COL', 'IDX_SPEC_5'],
];
// Returns cols with each Spec N column's label replaced by the IDX_SPEC_N_COL value in firstRow.
// IDX_SPEC_N_COL has fkSourceField so the backend only returns it as {camel}_Display, not raw.
const applyIndexSpecLabels = (cols, firstRow) =>
  cols.map(c => {
    const entry = INDEX_SPEC_COL_MAP.find(([, sf]) => sf === c.field);
    if (!entry || !firstRow) return c;
    const camelColField = toCamelCase(entry[0]);
    const label = firstRow[`${camelColField}_Display`] || firstRow[camelColField] || c.label;
    return { ...c, label };
  });

const ReportViewerPage = () => {
  const { reportSlug, dbSlug } = useParams();
  const location = useLocation();
  const { notify } = useNotifications();
  const { registerActivity, updateActivity, completeActivity, failActivity } = useDataTransfer();
  const dbContext = useDatabaseSafe();
  const dbName = localStorage.getItem('pmd-selected-database') || 'DB';

  const [config, setConfig] = useState(null);
  const [clientLogoSrc, setClientLogoSrc] = useState(null);
  const reportDisplayName = getReportMeta(reportSlug)?.label || config?.reportName || '';
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [executionMs, setExecutionMs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [parameters, setParameters] = useState({});
  const [committedParameters, setCommittedParameters] = useState({});
  const [hasRun, setHasRun] = useState(false);
  const [lastRunParams, setLastRunParams] = useState(null);

  // Sort state — 4 slots driving multi-level sort
  const EMPTY_SLOT = { field: '', dir: 'asc' };
  const [sortSlots, setSortSlots] = useState([EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT]);

  // Pagination state — page size derived from physical PDF page dimensions
  const [currentPage, setCurrentPage] = useState(1);
  // eslint-disable-next-line no-unused-vars
  const [selectedPageSize, setSelectedPageSize] = useState(null); // null = use config default

  // Fixed pixel widths for parameter header columns — computed once from all possible values
  const [paramHeaderWidths, setParamHeaderWidths] = useState({});
  // Pre-fetched options per field — passed to comboboxes so they render at correct width immediately
  const [paramOptions, setParamOptions] = useState({});
  // Gate: don't render comboboxes until param options are fetched (prevents resize jump on load)
  const [paramOptionsReady, setParamOptionsReady] = useState(false);

  // Export grouping state
  const [exportGroupBy, setExportGroupBy] = useState(null);
  const [exportGroupSeparate, setExportGroupSeparate] = useState(false);

  // Report options
  const [showQrCode, setShowQrCode] = useState(false);

  // Export action menu (6-dot header menu)
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  useEffect(() => {
    if (!exportMenuOpen) return;
    const handler = (e) => { if (!exportMenuRef.current?.contains(e.target)) setExportMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [exportMenuOpen]);

  // QR code data URL — generated from the current report URL + encoded parameters
  const [qrDataUrl, setQrDataUrl] = useState(null);

  // DAWN version string — fetched once, displayed in report footer
  const [dawnVersion, setDawnVersion] = useState('');
  useEffect(() => {
    devFilesApi.getRevLog()
      .then(res => setDawnVersion(res.data?.currentVersion || ''))
      .catch(() => {});
  }, []);

  // Tracks which reportSlug the currently loaded config belongs to — prevents stale-config
  // param fetches when navigating report-to-report (sibling effects see pre-setState values)
  const configSlugRef = useRef(null);
  const defaultSortRef = useRef([]);
  // Holds compact {field:[id]} map from ?p= QR URL — resolved after options load
  const urlParamsRef = useRef(null);

  // Scale entire preview (header + table + footer) as one unit — zoom-independent
  const previewContainerRef = useRef(null);
  const previewContentRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);

  // Load report config from API
  useEffect(() => {
    if (!reportSlug) return;

    const meta = getReportMeta(reportSlug);
    if (!meta) {
      notify.error(`Unknown report: ${reportSlug}`, 'error');
      setConfigLoading(false);
      return;
    }

    // Reset all run state when navigating to a different report
    setData([]);
    setTotalCount(0);
    setExecutionMs(null);
    setHasRun(false);
    setLastRunParams(null);
    setCurrentPage(1);
    setConfig(null);
    setExportGroupBy(null);
    setExportGroupSeparate(false);
    setShowQrCode(false);
    setCommittedParameters({});
    configSlugRef.current = null;
    urlParamsRef.current = null;
    setParamHeaderWidths({});
    setParamOptions({});
    setParamOptionsReady(false);

    setConfigLoading(true);
    reportingApi.getConfig(meta.configId)
      .then(res => {
        configSlugRef.current = reportSlug;
        const PARAM_ORDER = {
          Source: 1, Drawing: 2, Network: 3, Parent: 4,
          Panel: 5, Subnet: 5, Equipment: 6, Index: 7, Tag: 8, 'Port Type': 9
        };
        const sortedConfig = {
          ...res.data,
          parameters: [...(res.data.parameters || [])].sort((a, b) => {
            const oa = PARAM_ORDER[a.field] ?? PARAM_ORDER[a.label] ?? 99;
            const ob = PARAM_ORDER[b.field] ?? PARAM_ORDER[b.label] ?? 99;
            return oa - ob;
          })
        };
        setConfig(sortedConfig);
        // Force fixed grouping for reports that always group by a specific field
        if (FIXED_GROUP_BY[reportSlug]) setExportGroupBy(FIXED_GROUP_BY[reportSlug]);
        const savedParams = location?.state?.params || null;

        // If the URL contains ?p=<base64>, stash the compact {field:[id]} map in a ref.
        // Actual reconstruction happens after paramOptions load (we need display names).
        try {
          const pParam = new URLSearchParams(window.location.search).get('p');
          if (pParam) urlParamsRef.current = JSON.parse(atob(pParam));
        } catch (_) { /* ignore malformed */ }

        // Start with defaults (or router state); QR params override after options load
        const initialParams = {};
        (res.data.parameters || []).forEach(p => {
          initialParams[p.field] = savedParams?.[p.field]
            ?? [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
        });
        setParameters(initialParams);

        const configSort = (res.data.sorting || []).map(s => ({
          field: s.field,
          dir: (s.direction || 'asc').toLowerCase()
        }));
        defaultSortRef.current = configSort;
        const filled = [...configSort, EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT].slice(0, 4);
        setSortSlots(filled);
      })
      .catch(err => {
        notify.error(`Failed to load report config: ${err.message}`, 'error');
      })
      .finally(() => setConfigLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSlug]);

  // Fetch client logo and convert to base64 for PDF (worker needs serialisable data, not a URL).
  // Goes through /api/proxy/image to avoid CORS on the Azure blob storage origin.
  // Preview uses iconUrl directly — no fetch needed there.
  const clientIconUrl = dbContext?.currentDb?.iconUrl || null;
  const clientName = dbContext?.currentDb?.description || dbContext?.currentDb?.displayName || '';
  useEffect(() => {
    if (!clientIconUrl) { setClientLogoSrc(null); return; }
    const proxyUrl = `${API_BASE_URL}/api/proxy/image?url=${encodeURIComponent(clientIconUrl)}`;
    fetch(proxyUrl)
      .then(r => r.blob())
      .then(blob => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }))
      .then(dataUrl => setClientLogoSrc(dataUrl))
      .catch(() => setClientLogoSrc(null));
  }, [clientIconUrl]);

  // Auto-export trigger — when navigated from Activity History "Re-download"
  const autoExportTriggeredRef = useRef(false);
  useEffect(() => {
    const state = location?.state;
    if (!state?.autoExport || !config || autoExportTriggeredRef.current) return;
    autoExportTriggeredRef.current = true;
    const doAutoExport = async () => {
      await new Promise(r => setTimeout(r, 300));
      await runReport(state.params || undefined);
      await new Promise(r => setTimeout(r, 500));
      // eslint-disable-next-line no-use-before-define
      if (state.autoExport === 'pdf')   handleExportPdf();
      // eslint-disable-next-line no-use-before-define
      if (state.autoExport === 'excel') handleExportExcel();
      // eslint-disable-next-line no-use-before-define
      if (state.autoExport === 'csv')   handleExportCsv();
    };
    doAutoExport();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, location?.state]);

  // After config loads, fetch all param options and compute fixed column widths.
  // Guard: configSlugRef.current must match reportSlug — prevents stale-config fetches
  // when navigating report-to-report (React batches sibling effect state updates, so
  // config still holds the previous report's value when this effect fires after slug change).
  useEffect(() => {
    if (!config || !reportSlug) return;
    if (configSlugRef.current !== reportSlug) return;
    const params = config.parameters || [];
    if (params.length === 0) return;

    Promise.all(
      params.map(p =>
        reportingApi.getParameterOptions(reportSlug, p.field, null)
          .then(res => ({ field: p.field, label: p.label, options: res.data || [] }))
          .catch(() => ({ field: p.field, label: p.label, options: [] }))
      )
    ).then(results => {
      const widths = {};
      results.forEach(({ field, label, options }) => {
        // Size column by character count, capped at 150px. 1 char ≈ 1px.
        // For fullDisplay fields (headerDisplayFormat concatenations like "NUM - DESC"),
        // parse on " - " and track prefix/suffix lengths independently so that the
        // worst-case combination (longest number from one row + longest desc from another)
        // is used rather than a single row's total length.
        const hasFullDisplay = options.some(o => o.fullDisplay);
        let maxChars = label.length;
        if (hasFullDisplay) {
          let maxPrefixLen = 0, maxSuffixLen = 0;
          options.forEach(o => {
            const fd = String(o.fullDisplay || o.display || '');
            const sepIdx = fd.indexOf(' - ');
            if (sepIdx >= 0) {
              maxPrefixLen = Math.max(maxPrefixLen, sepIdx);
              maxSuffixLen = Math.max(maxSuffixLen, fd.length - sepIdx - 3);
            } else {
              maxPrefixLen = Math.max(maxPrefixLen, fd.length);
            }
          });
          maxChars = Math.max(maxChars, maxPrefixLen + (maxSuffixLen > 0 ? 3 + Math.min(maxSuffixLen, 70) : 0));
        } else {
          options.forEach(o => {
            maxChars = Math.max(maxChars, String(o.display || '').length);
          });
        }
        // Store as PDF pt directly (4pt Helvetica × 0.625 char ratio = 2.5pt/char, +6 = paddingLeft).
        // 0.625 calibrated for uppercase engineering labels (panel IDs, tag names, etc).
        // getW() in ReportPdfDocument uses these as-is. Preview uses them as approximate px (clips OK).
        widths[field] = Math.max(40, Math.min(Math.round(maxChars * 2.5) + 6, 310));
      });
      setParamHeaderWidths(widths);
      const optMap = {};
      results.forEach(({ field, options }) => { optMap[field] = options; });
      setParamOptions(optMap);
      setParamOptionsReady(true);

      // Resolve QR ?p= compact params now that we have display names
      if (urlParamsRef.current) {
        const compact = urlParamsRef.current;
        urlParamsRef.current = null;
        const resolved = {};
        (config.parameters || []).forEach(p => {
          const ids = compact[p.field];
          if (ids && optMap[p.field]) {
            const matched = optMap[p.field].filter(opt =>
              ids.includes(String(opt.id ?? opt.value ?? ''))
            );
            resolved[p.field] = matched.length > 0
              ? matched.map(opt => ({
                  type: 'item',
                  id: opt.id ?? opt.value,
                  display: opt.display,
                  fullDisplay: opt.fullDisplay || opt.display,
                  operator: 'eq',
                  value: opt.value ?? opt.id
                }))
              : [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
          } else {
            resolved[p.field] = [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
          }
        });
        setParameters(resolved);
      }
    });

    // No params — ready immediately
    if (params.length === 0) setParamOptionsReady(true);
  }, [config, reportSlug]);

  // Derive sortOrder from the 4 slot selects
  const sortOrder = useMemo(() =>
    sortSlots.filter(s => s.field).map(s => ({ field: s.field, direction: s.dir })),
  [sortSlots]);

  // Sort data client-side
  const sortedData = useMemo(() => {
    if (sortOrder.length === 0) return data;

    return [...data].sort((a, b) => {
      for (const { field, direction } of sortOrder) {
        const camelField = toCamelCase(field);
        const displayKey = `${camelField}_Display`;
        const aVal = a[displayKey] != null ? a[displayKey] : a[camelField];
        const bVal = b[displayKey] != null ? b[displayKey] : b[camelField];

        if (aVal == null && bVal == null) continue;
        if (aVal == null) return direction === 'asc' ? 1 : -1;
        if (bVal == null) return direction === 'asc' ? -1 : 1;

        let cmp;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          cmp = aVal - bVal;
        } else {
          cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
        }

        if (cmp !== 0) return direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [data, sortOrder]);

  const exportGroupCount = useMemo(() => {
    if (!exportGroupBy || !sortedData.length) return 0;
    const cf = toCamelCase(exportGroupBy);
    const seen = new Set();
    sortedData.forEach(row => {
      const val = row[`${cf}_Display`] ?? row[cf];
      seen.add(val != null ? String(val) : '(Blank)');
    });
    return seen.size;
  }, [exportGroupBy, sortedData]);

  // Column widths for preview — same content-aware algorithm as PDF export so
  // the preview table layout matches the PDF exactly (percentages of 1176pt content area).
  const PDF_CONTENT_WIDTH_PT = 1176; // TABLOID landscape 1224pt minus 24pt × 2 horizontal padding
  const previewColWidths = useMemo(() => {
    if (!config || !sortedData.length) return {};
    const isIndexRpt = !!FIXED_GROUP_BY[reportSlug];
    const allCols = [...(config.columns || [])].sort((a, b) => a.order - b.order);
    // Index reports: exclude the group field and the spec-label columns from the width table
    const cols = isIndexRpt ? allCols.filter(c => !INDEX_REPORT_HIDDEN_FIELDS.has(c.field)) : allCols;
    // Always measure at 8px — the preview <td> renders at 8px regardless of PDF font size.
    // PDF export has its own separate width calibration (6pt/ptFactor) in the exporter.
    const dataFont = '8px Helvetica, sans-serif';
    const headerFont = 'bold 8px Helvetica, sans-serif';
    const CELL_PAD_PT = 4;
    const WRAPPABLE_FIELDS = new Set(['Note', 'Comment']);
    const minWidths = {};
    cols.forEach(c => {
      const camelField = toCamelCase(c.field);
      const displayKey = `${camelField}_Display`;
      const isWrappable = WRAPPABLE_FIELDS.has(c.field);
      let maxDataPx = 0;
      if (!isWrappable) {
        sortedData.forEach(row => {
          const val = row[displayKey] != null ? row[displayKey] : row[camelField];
          if (val != null) maxDataPx = Math.max(maxDataPx, measureTextPx(String(val), dataFont));
        });
      }
      // For Index report spec columns, header label = IDX_SPEC_N_COL value (varies per group).
      // Measure the widest label across all rows so the column fits any group's header.
      let headerPx;
      if (isIndexRpt) {
        const specEntry = INDEX_SPEC_COL_MAP.find(([, sf]) => sf === c.field);
        if (specEntry) {
          // fkSourceField columns come back as {camel}_Display — check that key first
          const camelColField = toCamelCase(specEntry[0]);
          let maxLabelPx = 0;
          sortedData.forEach(row => {
            const lbl = row[`${camelColField}_Display`] || row[camelColField];
            if (lbl) maxLabelPx = Math.max(maxLabelPx, measureTextPx(String(lbl), headerFont));
          });
          headerPx = maxLabelPx || measureTextPx(c.label, headerFont);
        }
      }
      if (headerPx == null) {
        const [hLine1, hLine2] = splitHeaderLabel(c.label);
        headerPx = Math.max(measureTextPx(hLine1, headerFont), hLine2 ? measureTextPx(hLine2, headerFont) : 0);
      }
      minWidths[c.field] = Math.max(67, Math.ceil(Math.max(maxDataPx, headerPx)) + CELL_PAD_PT);
    });
    // Wrappable column floors: Note ≥ Description width; Comment ≥ 333px
    if (minWidths.Note != null) {
      minWidths.Note = Math.max(minWidths.Note, minWidths.Description || minWidths.Tag_Description || 67);
    }
    if (minWidths.Comment != null) {
      minWidths.Comment = Math.max(minWidths.Comment, 233);
    }
    const totalMinPt = cols.reduce((sum, c) => sum + minWidths[c.field], 0);
    const colWidths = {};
    if (totalMinPt >= PDF_CONTENT_WIDTH_PT) {
      cols.forEach(c => { colWidths[c.field] = `${((minWidths[c.field] / totalMinPt) * 100).toFixed(1)}%`; });
    } else {
      const remaining = PDF_CONTENT_WIDTH_PT - totalMinPt;
      const totalConfigPx = cols.reduce((sum, c) => sum + (c.pixelWidth || c.width * 10 || 100), 0);
      cols.forEach(c => {
        const configShare = (c.pixelWidth || c.width * 10 || 100) / totalConfigPx;
        const extraPt = remaining * configShare;
        colWidths[c.field] = `${(((minWidths[c.field] + extraPt) / PDF_CONTENT_WIDTH_PT) * 100).toFixed(1)}%`;
      });
    }
    return colWidths;
  }, [config, sortedData, reportSlug]);

  // URL embedded in the QR code — compact {field:[id]} map, skipping __all__ defaults.
  // Stripping display names and default fields keeps the payload tiny so the QR code
  // stays at a low version (sparse pattern = easier to scan and print at low DPI).
  const reportUrl = useMemo(() => {
    if (!hasRun || !config) return null;
    const compact = {};
    Object.entries(parameters).forEach(([field, sels]) => {
      // Skip fields where the only selection is the "All" default
      if (sels.length === 1 && sels[0].id === '__all__') return;
      compact[field] = sels.map(s => String(s.id));
    });
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(compact))));
    return `${window.location.origin}${window.location.pathname}?p=${encoded}`;
  }, [hasRun, config, parameters]);

  // Regenerate QR data URL whenever the report URL changes
  useEffect(() => {
    if (!reportUrl) { setQrDataUrl(null); return; }
    QRCode.toDataURL(reportUrl, {
      width: 400, margin: 2, // 400px source — prints cleanly at 300dpi up to ~1.3"
      color: { dark: '#1a5568', light: '#ffffff' }
    }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
  }, [reportUrl]);

  // Calculate rows per page from actual PDF layout measurements (points)
  const pageSize = useMemo(() => {
    if (!config?.layout) return 47;

    // Page dimensions in points (72pt = 1 inch), landscape orientation
    const pageDimensions = {
      letter:  { width: 792, height: 612 },   // 11x8.5
      tabloid: { width: 1224, height: 792 }    // 17x11
    };

    const dims = pageDimensions[config.layout.defaultPageSize] || pageDimensions.tabloid;

    // Fixed measurements matching ReportPdfStyles.js exactly
    const pageMarginTop = 30;
    const pageMarginBottom = 40;
    const headerHeight = 115;     // worst-case: 2 param rows × maxHeight(48) + margins/border(19)
    const colHeaderHeight = 16;   // paddingV(8) + fontSize(6.5) + border(1.5)
    const fontSize = config.layout.fontSize || 8;
    const rowHeight = fontSize + 5.5;  // fontSize + paddingV(2.5×2) + border(0.5)

    const usableHeight = dims.height - pageMarginTop - pageMarginBottom - headerHeight - colHeaderHeight;
    const rowsPerPage = Math.floor(usableHeight / rowHeight);

    const calculated = Math.max(10, rowsPerPage);
    return Math.min(calculated, 40);
  }, [config]);

  // Grouped page descriptors — built when Group By is active
  const groupedPages = useMemo(() => {
    if (!exportGroupBy || !hasRun || !sortedData.length) return null;
    const cf = toCamelCase(exportGroupBy);
    const displayKey = `${cf}_Display`;
    const groupColLabel = config?.parameters?.find(p => p.field === exportGroupBy)?.label
      || config?.columns?.find(c => c.field === exportGroupBy)?.label
      || exportGroupBy;
    const groupMap = new Map();
    sortedData.forEach(row => {
      const val = row[displayKey] != null ? row[displayKey] : row[cf];
      const key = val != null ? String(val) : '(Blank)';
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key).push(row);
    });
    const pages = [];
    [...groupMap.entries()]
      .sort(([a], [b]) => {
        if (a === '(Blank)') return 1;
        if (b === '(Blank)') return -1;
        return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
      })
      .forEach(([groupVal, groupRows]) => {
        const subtitle = `(${groupColLabel}: ${groupVal})`;
        for (let i = 0; i < Math.max(groupRows.length, 1); i += pageSize) {
          pages.push({ subtitle, groupRowCount: groupRows.length, rows: groupRows.slice(i, i + pageSize) });
        }
      });
    return pages.length > 0 ? pages : null;
  }, [exportGroupBy, sortedData, pageSize, config, hasRun]);

  // Paginate sorted data
  const totalPages = groupedPages
    ? groupedPages.length
    : Math.max(1, Math.ceil(sortedData.length / pageSize));
  const paginatedData = useMemo(() => {
    if (groupedPages) return groupedPages[currentPage - 1]?.rows || [];
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [groupedPages, sortedData, currentPage, pageSize]);

  // Run report
  const runReport = useCallback(async (overrideParams) => {
    if (!config) return;

    setLoading(true);
    try {
      const paramPayload = {};
      Object.entries(overrideParams ?? parameters).forEach(([field, selections]) => {
        const camelField = toCamelCase(field);
        // "All" means no filter on this field — exclude from payload entirely
        const filtered = (selections || []).filter(s => s.id !== '__all__');
        if (filtered.length > 0) {
          paramPayload[camelField] = filtered.map(s => ({
            operator: s.operator,
            value: s.value
          }));
        }
      });

      const res = await reportingApi.runReport(reportSlug, { parameters: paramPayload });
      setData(res.data.data);
      setTotalCount(res.data.totalCount);
      setExecutionMs(res.data.executionMs);
      setHasRun(true);
      setCommittedParameters({ ...parameters });
      setLastRunParams(JSON.stringify(parameters));
      setCurrentPage(1);
    } catch (err) {
      notify.error(`Report failed: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [config, parameters, notify, reportSlug]);

  // Recalculate preview scale whenever data/page/zoom changes.
  // Only active after a report has been run — pre-run the header is a flex layout
  // with no minimum width anchor so scale thrashes. Lock to 1 until data is loaded.
  useLayoutEffect(() => {
    if (!hasRun) {
      setPreviewScale(1);
      return;
    }

    const calcScale = () => {
      if (!previewContainerRef.current || !previewContentRef.current) return;
      // Measure natural content width directly — the width compensation formula
      // (width: 100/previewScale %) means scrollWidth already equals natural width,
      // so no need to reset to scale=1 first (that reset caused a visible flash).
      requestAnimationFrame(() => {
        if (!previewContainerRef.current || !previewContentRef.current) return;
        const containerWidth = previewContainerRef.current.clientWidth;
        const contentWidth = previewContentRef.current.scrollWidth;
        setPreviewScale(contentWidth > containerWidth ? containerWidth / contentWidth : 1);
      });
    };

    calcScale();
    window.addEventListener('resize', calcScale);
    return () => window.removeEventListener('resize', calcScale);
  }, [paginatedData, hasRun]);

  // Reset to page 1 when grouping selection changes
  useEffect(() => { setCurrentPage(1); }, [exportGroupBy]);

  const updateParameter = (field, selections) => {
    setParameters(prev => {
      const updated = { ...prev, [field]: selections };
      const ALL_SELECTION = [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
      const hasSpecific = (sels) => (sels || []).some(s => s.type === 'picked' || s.type === 'freetext');
      // For every other parameter: if it has no specific selection, reset it to "All"
      (config?.parameters || []).forEach(p => {
        if (p.field === field) return;
        if (!hasSpecific(prev[p.field])) {
          updated[p.field] = ALL_SELECTION;
        }
      });
      return updated;
    });
  };

  const clearFiltersAndSort = () => {
    const ALL = [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
    const reset = {};
    (config?.parameters || []).forEach(p => { reset[p.field] = ALL; });
    setParameters(reset);
    setSortSlots([EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT]);
  };

  const resetToDefaults = () => {
    const ALL = [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
    const reset = {};
    (config?.parameters || []).forEach(p => { reset[p.field] = ALL; });
    setParameters(reset);
    const filled = [...defaultSortRef.current, EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT].slice(0, 4);
    setSortSlots(filled);
  };

  const activeFilterCount = Object.values(parameters).reduce((sum, s) =>
    sum + (s || []).filter(x => x.id !== '__all__').length, 0);
  const isCurrent = hasRun && lastRunParams === JSON.stringify(parameters);
  const [exportingType, setExportingType] = useState(null);

  // Section collapse state — all expanded by default
  const [sec1Collapsed, setSec1Collapsed] = useState(false);
  const [sec2Collapsed, setSec2Collapsed] = useState(false);
  const cancelExportRef = useRef(false);
  const activeWorkerRef = useRef(null); // { worker, reject } — set while PDF worker is running
  const exportTimerRef  = useRef(null); // setInterval handle for progress ticking

  const handleCancelExport = useCallback(() => {
    cancelExportRef.current = true;
    if (exportTimerRef.current) { clearInterval(exportTimerRef.current); exportTimerRef.current = null; }
    // Immediately terminate the PDF worker if it's running
    if (activeWorkerRef.current) {
      activeWorkerRef.current.worker.terminate();
      activeWorkerRef.current.reject(new DOMException('Export cancelled by user', 'AbortError'));
      activeWorkerRef.current = null;
    }
    setExportingType(null);
    notify.info(config ? `${dbName} - ${reportDisplayName} Report - PDF - Cancelled` : 'Export cancelled.', 3000);
  }, [notify, config, dbName, reportDisplayName]);

  // Clear all filters — reset each parameter back to "All"
  // PDF export
  const handleExportPdf = useCallback(async () => {
    if (!config || sortedData.length === 0) return;
    cancelExportRef.current = false;

    // ── Timer setup ──────────────────────────────────────────────────────────
    const _start  = Date.now();

    notify.status(`${dbName} - ${reportDisplayName} Report - PDF - Started`, 0);
    // ─────────────────────────────────────────────────────────────────────────

    const isIndexRpt = !!FIXED_GROUP_BY[reportSlug];
    const cols = [...(config.columns || [])].sort((a, b) => a.order - b.order)
      .filter(c => !isIndexRpt || !INDEX_REPORT_HIDDEN_FIELDS.has(c.field));

    // Content-aware column widths — measure actual data values + header label,
    // then distribute remaining tabloid-landscape space by config proportion.
    // This guarantees every value fits on one line (style guide: no truncation, no 2-line rows).
    const PDF_PAGE_WIDTH_PT = 1176; // tabloid landscape 1224pt - 48pt padding
    const fontSize = reportSlug === 'slugRptStd-Gen-IO_Assignments' ? 6 : (config.layout?.fontSize || 8);
    const dataFont   = `${fontSize}px Helvetica, sans-serif`;
    const headerFont = `bold ${fontSize}px Helvetica, sans-serif`;
    const CELL_PAD_PT = 4; // left+right padding in PDF cells

    // toCamelCase mirrors the PDF renderer's field lookup
    const toCamelCasePdf = (name) => {
      if (!name) return name;
      if ([...name].every(c => (c >= 'A' && c <= 'Z') || c === '_')) return name.toLowerCase();
      return name.charAt(0).toLowerCase() + name.slice(1);
    };

    // Minimum width per column = max(header label width, longest data value width) + padding
    const WRAPPABLE_FIELDS_PDF = new Set(['Note', 'Comment']);
    const minWidths = {};
    cols.forEach(c => {
      const camelField = toCamelCasePdf(c.field);
      const displayKey = `${camelField}_Display`;
      const isWrappable = WRAPPABLE_FIELDS_PDF.has(c.field);
      let maxDataPx = 0;
      if (!isWrappable) {
        sortedData.forEach(row => {
          const val = row[displayKey] != null ? row[displayKey] : row[camelField];
          if (val != null) {
            maxDataPx = Math.max(maxDataPx, measureTextPx(String(val), dataFont));
          }
        });
      }
      // For Index report spec columns, header label = IDX_SPEC_N_COL value (varies per group).
      // Measure widest label across all rows so the column fits any group's header.
      let headerPx;
      if (isIndexRpt) {
        const specEntry = INDEX_SPEC_COL_MAP.find(([, sf]) => sf === c.field);
        if (specEntry) {
          // fkSourceField columns come back as {camel}_Display — check that key first
          const camelColField = toCamelCasePdf(specEntry[0]);
          let maxLabelPx = 0;
          sortedData.forEach(row => {
            const lbl = row[`${camelColField}_Display`] || row[camelColField];
            if (lbl) maxLabelPx = Math.max(maxLabelPx, measureTextPx(String(lbl), headerFont));
          });
          headerPx = maxLabelPx || measureTextPx(c.label, headerFont);
        }
      }
      if (headerPx == null) {
        const [hLine1, hLine2] = splitHeaderLabel(c.label);
        headerPx = Math.max(measureTextPx(hLine1, headerFont), hLine2 ? measureTextPx(hLine2, headerFont) : 0);
      }
      // IO Assignments uses 6pt font and was calibrated at 0.75 — leave it alone.
      // All other reports use 8pt font: PDF renders at fontSize pt but we measure at fontSize px
      // (CSS px ≈ 0.75pt), so measured width is ~25% too narrow. Use 1.0 to avoid clipping.
      const ptFactor = reportSlug === 'slugRptStd-Gen-IO_Assignments' ? 0.75 : 1.0;
      minWidths[c.field] = Math.max(40, Math.ceil(Math.max(maxDataPx, headerPx) * ptFactor) + CELL_PAD_PT);
    });
    // Wrappable column floors: Note ≥ Description width; Comment ≥ 250pt
    if (minWidths.Note != null) {
      minWidths.Note = Math.max(minWidths.Note, minWidths.Description || minWidths.Tag_Description || 50);
    }
    if (minWidths.Comment != null) {
      minWidths.Comment = Math.max(minWidths.Comment, 175);
    }

    const totalMinPt = cols.reduce((sum, c) => sum + minWidths[c.field], 0);
    const colWidths = {};

    if (totalMinPt >= PDF_PAGE_WIDTH_PT) {
      // Mins already fill the page — just use them as-is
      cols.forEach(c => { colWidths[c.field] = `${((minWidths[c.field] / totalMinPt) * 100).toFixed(1)}%`; });
    } else {
      // Distribute remaining space proportionally by config pixelWidth preference
      const remaining = PDF_PAGE_WIDTH_PT - totalMinPt;
      const totalConfigPx = cols.reduce((sum, c) => sum + (c.pixelWidth || c.width * 10 || 100), 0);
      cols.forEach(c => {
        const configShare = (c.pixelWidth || c.width * 10 || 100) / totalConfigPx;
        const extraPt = remaining * configShare;
        colWidths[c.field] = `${(((minWidths[c.field] + extraPt) / PDF_PAGE_WIDTH_PT) * 100).toFixed(1)}%`;
      });
    }

    // For Index reports: build per-group column configs with dynamic spec headers.
    // Combined PDF passes columnsByGroup so ReportPdfDocument can change headers per group page.
    // Separate-files export uses per-group cols directly (no need for columnsByGroup there).
    let columnsByGroup = null;
    if (isIndexRpt && exportGroupBy) {
      const groupCf = toCamelCasePdf(exportGroupBy);
      const firstRowByGroup = {};
      sortedData.forEach(row => {
        const val = row[`${groupCf}_Display`] != null ? row[`${groupCf}_Display`] : row[groupCf];
        const key = val != null ? String(val) : '(Blank)';
        if (!firstRowByGroup[key]) firstRowByGroup[key] = row;
      });
      columnsByGroup = {};
      Object.entries(firstRowByGroup).forEach(([groupVal, firstRow]) => {
        columnsByGroup[groupVal] = applyIndexSpecLabels(cols, firstRow);
      });
    }

    // For Index reports: strip Index_Sheet from header params (shown as subtitle already).
    const pdfHeaderConfig = isIndexRpt
      ? { ...config, parameters: (config.parameters || []).filter(p => p.field !== 'Index_Sheet') }
      : config;

    // ── Data Transfer registration ───────────────────────────────────────────
    const activityName = `${dbName} - ${reportDisplayName} Report`;
    const exportRecipe = {
      reportId:    reportSlug,
      reportName:  reportDisplayName,
      reportPath:  `/reports/${reportSlug}`,
      format:      'pdf',
      filterParams: parameters,
      fileName:    buildReportFilename(reportDisplayName, 'pdf'),
      dbSlug:      dbSlug || localStorage.getItem('pmd-selected-db-slug') || 'gwa',
      dbName:      dbName,
    };
    registerActivity(`pdf-${reportSlug}`, {
      name: activityName, type: 'download', format: 'pdf',
      phase: 'Preparing…', pct: 0,
      restartFn: () => handleExportPdf(),
      exportRecipe,
    });

    // ── Estimated duration + progress timer ─────────────────────────────────
    const pageCount = sortedData.length > 0
      ? Math.ceil(sortedData.length / (reportSlug === 'slugRptStd-Gen-IO_Assignments' ? 40 : 30))
      : 1;
    const _estSec = Math.max(5, pageCount * 0.8);
    let _phaseLabel = 'Building…';

    if (exportTimerRef.current) clearInterval(exportTimerRef.current);
    exportTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - _start) / 1000;
      const pct = Math.min(94, Math.round((elapsed / _estSec) * 100));
      updateActivity(`pdf-${reportSlug}`, { phase: _phaseLabel, pct });
    }, 1000);

    const onPhase = (phase) => {
      switch (phase) {
        case 'building':   _phaseLabel = 'Building document…'; break;
        case 'rendering':  _phaseLabel = `Rendering ${pageCount} pages…`; break;
        case 'download':   _phaseLabel = 'Writing file…'; break;
        case 'ready': {
          clearInterval(exportTimerRef.current); exportTimerRef.current = null;
          notify.success(`${dbName} - ${reportDisplayName} Report - PDF - Finished`, 5000);
          completeActivity(`pdf-${reportSlug}`);
          break;
        }
        case 'cancelled':
          clearInterval(exportTimerRef.current); exportTimerRef.current = null;
          notify.info(`${dbName} - ${reportDisplayName} Report - PDF - Cancelled`, 3000);
          failActivity(`pdf-${reportSlug}`);
          break;
        default: break;
      }
    };

    try {
      // All reports: 40 rows/page
      const effectiveRowsPerPage = 40;

      // Phase handler that defers the success/complete signals during multi-file loops
      const onPhaseSilent = (phase) => {
        switch (phase) {
          case 'building':  _phaseLabel = 'Building document…'; break;
          case 'rendering': _phaseLabel = `Rendering pages…`; break;
          case 'download':  _phaseLabel = 'Writing file…'; break;
          case 'ready':     break; // deferred — handled after loop
          case 'cancelled':
            clearInterval(exportTimerRef.current); exportTimerRef.current = null;
            notify.info(`${dbName} - ${reportDisplayName} Report - PDF - Cancelled`, 3000);
            failActivity(`pdf-${reportSlug}`);
            break;
          default: break;
        }
      };

      if (exportGroupBy && exportGroupSeparate) {
        // Separate PDF file per unique group value — auto-download each
        const groupMap = {};
        sortedData.forEach(row => {
          const cf = toCamelCasePdf(exportGroupBy);
          const val = row[`${cf}_Display`] != null ? row[`${cf}_Display`] : row[cf];
          const key = val != null ? String(val) : '(Blank)';
          if (!groupMap[key]) groupMap[key] = [];
          groupMap[key].push(row);
        });
        const _pdfBatchTs = Date.now();
        let _pdfPageIdx = 0;
        for (const [groupVal, groupRows] of Object.entries(groupMap).sort(([a], [b]) => {
          if (a === '(Blank)') return 1;
          if (b === '(Blank)') return -1;
          return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
        })) {
          if (cancelExportRef.current) break;
          // For Index reports: each separate file gets columns with that group's dynamic spec labels
          const groupSpecificCols = columnsByGroup?.[groupVal] || cols;
          const pageReportName = `${reportDisplayName} - ${groupVal}`;
          const result = await exportReportPdf({
            config: { ...pdfHeaderConfig, reportName: pageReportName },
            columns: groupSpecificCols,
            columnWidths: colWidths,
            data: groupRows,
            parameters,
            paramHeaderWidths,
            logoSrc: wmLogo,
            clientLogoSrc: clientLogoSrc || undefined,
            clientName: clientName || undefined,
            sortOrder: sortOrder.length > 0 ? sortOrder : undefined,
            groupByField: exportGroupBy, // still passed so header subtitle renders per page
            rowsPerPage: effectiveRowsPerPage,
            fontSizeOverride: reportSlug === 'slugRptStd-Gen-IO_Assignments' ? 6 : undefined,
            qrDataUrl: (showQrCode && qrDataUrl) || undefined,
            dawnVersion: dawnVersion || undefined,
            autoDownload: true,
            isCancelled: () => cancelExportRef.current,
            onWorkerReady: (worker, reject) => { activeWorkerRef.current = { worker, reject }; },
            onPhase: onPhaseSilent,
            onProgress: (msg) => setExportingType(msg ? 'pdf' : null)
          });
          activeWorkerRef.current = null;
          if (result === false || cancelExportRef.current) break;
          const pageId = `pdf-${reportSlug}-${_pdfBatchTs}-${_pdfPageIdx++}`;
          registerActivity(pageId, {
            name: `${dbName} - ${pageReportName}`, type: 'download', format: 'pdf',
            exportRecipe: {
              reportId: reportSlug, reportName: pageReportName,
              reportPath: `/reports/${reportSlug}`, format: 'pdf',
              filterParams: parameters, fileName: buildReportFilename(pageReportName, 'pdf'),
              dbSlug: dbSlug || localStorage.getItem('pmd-selected-db-slug') || 'gwa', dbName,
            },
          });
          completeActivity(pageId);
        }
        if (exportTimerRef.current) { clearInterval(exportTimerRef.current); exportTimerRef.current = null; }
        if (!cancelExportRef.current) {
          notify.success(`${dbName} - ${reportDisplayName} Report - PDF - Finished`, 5000);
          completeActivity(`pdf-${reportSlug}`);
        }
      } else {
        const groupedLabel = (exportGroupBy && !exportGroupSeparate)
          ? (config?.parameters?.find(p => p.field === exportGroupBy)?.label
              || config?.columns?.find(c => c.field === exportGroupBy)?.label
              || exportGroupBy)
          : undefined;
        const result = await exportReportPdf({
          config: { ...pdfHeaderConfig, reportName: reportDisplayName },
          columns: cols,
          columnWidths: colWidths,
          data: sortedData,
          parameters,
          paramHeaderWidths,
          logoSrc: wmLogo,
          clientLogoSrc: clientLogoSrc || undefined,
          clientName: clientName || undefined,
          sortOrder: sortOrder.length > 0 ? sortOrder : undefined,
          groupByField: exportGroupBy || undefined,
          groupByLabel: groupedLabel,
          rowsPerPage: effectiveRowsPerPage,
          fontSizeOverride: reportSlug === 'slugRptStd-Gen-IO_Assignments' ? 6 : undefined,
          qrDataUrl: (showQrCode && qrDataUrl) || undefined,
          dawnVersion: dawnVersion || undefined,
          columnsByGroup: columnsByGroup || undefined,
          isCancelled: () => cancelExportRef.current,
          onWorkerReady: (worker, reject) => { activeWorkerRef.current = { worker, reject }; },
          onPhase,
          onProgress: (msg) => setExportingType(msg ? 'pdf' : null)
        });
        activeWorkerRef.current = null;
        if (exportTimerRef.current) { clearInterval(exportTimerRef.current); exportTimerRef.current = null; }
        // Exporter returns false when user cancels the save dialog — treat as cancellation
        if (result === false) {
          setExportingType(null);
          failActivity(`pdf-${reportSlug}`);
        }
      }
    } catch (err) {
      if (exportTimerRef.current) { clearInterval(exportTimerRef.current); exportTimerRef.current = null; }
      console.error('PDF export failed:', err);
      setExportingType(null);
      failActivity(`pdf-${reportSlug}`);
    }
  }, [config, sortedData, sortOrder, parameters, paramHeaderWidths, exportGroupBy, exportGroupSeparate, reportSlug, dbSlug, notify, dbName, reportDisplayName, clientLogoSrc, clientName, showQrCode, qrDataUrl, dawnVersion, registerActivity, updateActivity, completeActivity, failActivity]);

  // Excel export
  const handleExportExcel = useCallback(async () => {
    if (!config || sortedData.length === 0) return;
    cancelExportRef.current = false;
    const cols = [...(config.columns || [])].sort((a, b) => a.order - b.order);
    const actId = `excel-${reportSlug}`;
    registerActivity(actId, {
      name: `${dbName} - ${reportDisplayName} Report`, type: 'download', format: 'excel',
      phase: 'Generating Excel…', pct: 0,
      restartFn: () => handleExportExcel(),
      exportRecipe: { reportId: reportSlug, reportName: reportDisplayName, reportPath: `/reports/${reportSlug}`, format: 'excel', filterParams: parameters, fileName: buildReportFilename(reportDisplayName, 'xlsx'), dbSlug: dbSlug || localStorage.getItem('pmd-selected-db-slug') || 'gwa', dbName },
    });
    notify.status(`${dbName} - ${reportDisplayName} Report - Excel - Started`, 0);
    try {
      if (exportGroupBy && exportGroupSeparate) {
        // Separate file per group — auto-download each
        const groupMap = {};
        sortedData.forEach(row => {
          const cf = toCamelCase(exportGroupBy);
          const val = row[`${cf}_Display`] != null ? row[`${cf}_Display`] : row[cf];
          const key = val != null ? String(val) : '(Blank)';
          if (!groupMap[key]) groupMap[key] = [];
          groupMap[key].push(row);
        });
        const _xlsBatchTs = Date.now();
        let _xlsPageIdx = 0;
        for (const [groupVal, groupRows] of Object.entries(groupMap).sort(([a], [b]) => {
          if (a === '(Blank)') return 1;
          if (b === '(Blank)') return -1;
          return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
        })) {
          if (cancelExportRef.current) break;
          const pageReportName = `${reportDisplayName} - ${groupVal}`;
          await exportReportExcel({
            config: { ...config, reportName: pageReportName },
            columns: cols, data: groupRows, parameters,
            autoDownload: true,
            isCancelled: () => cancelExportRef.current,
            onProgress: (msg) => setExportingType(msg ? 'excel' : null)
          });
          if (cancelExportRef.current) break;
          const pageId = `excel-${reportSlug}-${_xlsBatchTs}-${_xlsPageIdx++}`;
          registerActivity(pageId, {
            name: `${dbName} - ${pageReportName}`, type: 'download', format: 'excel',
            exportRecipe: {
              reportId: reportSlug, reportName: pageReportName,
              reportPath: `/reports/${reportSlug}`, format: 'excel',
              filterParams: parameters, fileName: buildReportFilename(pageReportName, 'xlsx'),
              dbSlug: dbSlug || localStorage.getItem('pmd-selected-db-slug') || 'gwa', dbName,
            },
          });
          completeActivity(pageId);
        }
      } else {
        await exportReportExcel({
          config: { ...config, reportName: reportDisplayName },
          columns: cols,
          data: sortedData,
          parameters,
          groupByField: exportGroupBy || undefined,
          isCancelled: () => cancelExportRef.current,
          onProgress: (msg) => setExportingType(msg ? 'excel' : null)
        });
      }
      notify.success(`${dbName} - ${reportDisplayName} Report - Excel - Finished`, 5000);
      completeActivity(actId);
    } catch (err) {
      console.error('Excel export failed:', err);
      notify.info(`${dbName} - ${reportDisplayName} Report - Excel - Cancelled`, 3000);
      setExportingType(null);
      failActivity(actId);
    }
  }, [config, sortedData, parameters, exportGroupBy, exportGroupSeparate, reportSlug, dbSlug, notify, dbName, reportDisplayName, registerActivity, completeActivity, failActivity]);

  // CSV export
  const handleExportCsv = useCallback(async () => {
    if (!config || sortedData.length === 0) return;
    cancelExportRef.current = false;
    const cols = [...(config.columns || [])].sort((a, b) => a.order - b.order);
    const actId = `csv-${reportSlug}`;
    registerActivity(actId, {
      name: `${dbName} - ${reportDisplayName} Report`, type: 'download', format: 'csv',
      phase: 'Generating CSV…', pct: 0,
      restartFn: () => handleExportCsv(),
      exportRecipe: { reportId: reportSlug, reportName: reportDisplayName, reportPath: `/reports/${reportSlug}`, format: 'csv', filterParams: parameters, fileName: buildReportFilename(reportDisplayName, 'csv'), dbSlug: dbSlug || localStorage.getItem('pmd-selected-db-slug') || 'gwa', dbName },
    });
    notify.status(`${dbName} - ${reportDisplayName} Report - CSV - Started`, 0);
    try {
      if (exportGroupBy && exportGroupSeparate) {
        const groupMap = {};
        sortedData.forEach(row => {
          const cf = toCamelCase(exportGroupBy);
          const val = row[`${cf}_Display`] != null ? row[`${cf}_Display`] : row[cf];
          const key = val != null ? String(val) : '(Blank)';
          if (!groupMap[key]) groupMap[key] = [];
          groupMap[key].push(row);
        });
        const _csvBatchTs = Date.now();
        let _csvPageIdx = 0;
        for (const [groupVal, groupRows] of Object.entries(groupMap).sort(([a], [b]) => {
          if (a === '(Blank)') return 1;
          if (b === '(Blank)') return -1;
          return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
        })) {
          if (cancelExportRef.current) break;
          const pageReportName = `${reportDisplayName} - ${groupVal}`;
          await exportReportCsv({
            config: { ...config, reportName: pageReportName },
            columns: cols, data: groupRows,
            autoDownload: true,
            isCancelled: () => cancelExportRef.current,
            onProgress: (msg) => setExportingType(msg ? 'csv' : null)
          });
          if (cancelExportRef.current) break;
          const pageId = `csv-${reportSlug}-${_csvBatchTs}-${_csvPageIdx++}`;
          registerActivity(pageId, {
            name: `${dbName} - ${pageReportName}`, type: 'download', format: 'csv',
            exportRecipe: {
              reportId: reportSlug, reportName: pageReportName,
              reportPath: `/reports/${reportSlug}`, format: 'csv',
              filterParams: parameters, fileName: buildReportFilename(pageReportName, 'csv'),
              dbSlug: dbSlug || localStorage.getItem('pmd-selected-db-slug') || 'gwa', dbName,
            },
          });
          completeActivity(pageId);
        }
      } else {
        await exportReportCsv({
          config: { ...config, reportName: reportDisplayName },
          columns: cols,
          data: sortedData,
          groupByField: exportGroupBy || undefined,
          isCancelled: () => cancelExportRef.current,
          onProgress: (msg) => setExportingType(msg ? 'csv' : null)
        });
      }
      notify.success(`${dbName} - ${reportDisplayName} Report - CSV - Finished`, 5000);
      completeActivity(actId);
    } catch (err) {
      console.error('CSV export failed:', err);
      notify.info(`${dbName} - ${reportDisplayName} Report - CSV - Cancelled`, 3000);
      setExportingType(null);
      failActivity(actId);
    }
  }, [config, sortedData, parameters, exportGroupBy, exportGroupSeparate, reportSlug, dbSlug, notify, dbName, reportDisplayName, registerActivity, completeActivity, failActivity]);

  if (configLoading || !paramOptionsReady) {
    return <div className="stui-module-loading">Loading report configuration...</div>;
  }

  if (!config) {
    return <div className="stui-module-loading">Report not found: {reportSlug}</div>;
  }

  const columns = [...(config.columns || [])].sort((a, b) => a.order - b.order);
  const isIndexReport = !!FIXED_GROUP_BY[reportSlug];
  // Preview/PDF columns with hidden fields removed (Index reports only)
  const displayColumns = isIndexReport
    ? columns.filter(c => !INDEX_REPORT_HIDDEN_FIELDS.has(c.field))
    : columns;
  // Header columns for the current preview page — spec labels replaced from the current group's first row
  const currentPageHeaderColumns = isIndexReport && paginatedData.length > 0
    ? applyIndexSpecLabels(displayColumns, paginatedData[0])
    : displayColumns;
  const canRun = !loading;
  const runLabel = loading ? 'Running...' : isCurrent ? 'Refresh' : 'Run Report';

  // Build sort lookup for column headers
  const sortLookup = {};
  sortOrder.forEach((s, i) => {
    sortLookup[s.field] = { direction: s.direction, priority: i + 1 };
  });

  return (
    <div className="stui-module-container stui-report-viewer">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <FileSpreadsheet size={20} />
          <h1>{reportDisplayName}</h1>
          {config.description && (
            <span className="stui-module-header-meta">{config.description}</span>
          )}
        </div>
        <div className="stui-module-header-actions">
        {/* 6-dot export menu */}
        <div className="stui-action-menu" ref={exportMenuRef}>
          <button
            className={`stui-action-menu-btn${(!hasRun || sortedData.length === 0) ? ' disabled' : ''}`}
            onClick={() => (hasRun && sortedData.length > 0) && setExportMenuOpen(o => !o)}
          >
            <GripVertical size={16} />
          </button>
          {exportMenuOpen && (
            <div className="stui-action-menu-dropdown">
              <label
                className="stui-action-menu-item"
                style={{ cursor: exportGroupBy ? 'pointer' : 'not-allowed', opacity: exportGroupBy ? 1 : 0.4 }}
                onClick={e => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="stui-checkbox-input"
                  checked={exportGroupSeparate}
                  disabled={!exportGroupBy}
                  onChange={e => setExportGroupSeparate(e.target.checked)}
                />
                <span>Separate Files{exportGroupBy && exportGroupCount > 0 ? ` (${exportGroupCount})` : ''}</span>
              </label>
              <div className="stui-action-menu-divider" />
              <button className="stui-action-menu-item" disabled={!!exportingType} onClick={() => { handleExportPdf(); setExportMenuOpen(false); }}>
                <Download size={16} /><span>Download PDF</span>
              </button>
              <button className="stui-action-menu-item" disabled={!!exportingType} onClick={() => { handleExportExcel(); setExportMenuOpen(false); }}>
                <Download size={16} /><span>Download Excel</span>
              </button>
              <button className="stui-action-menu-item" disabled={!!exportingType} onClick={() => { handleExportCsv(); setExportMenuOpen(false); }}>
                <Download size={16} /><span>Download CSV</span>
              </button>
              {exportingType && (
                <>
                  <div className="stui-action-menu-divider" />
                  <button className="stui-action-menu-item stui-action-menu-item-danger" onClick={() => { handleCancelExport(); setExportMenuOpen(false); }}>
                    <Ban size={16} /><span>Cancel Export</span>
                  </button>
                </>
              )}
              <div className="stui-action-menu-divider" />
              <button
                className="stui-action-menu-item"
                onClick={() => { resetToDefaults(); setExportMenuOpen(false); }}
              >
                <FilterX size={16} /><span>Default Filters &amp; Sort</span>
              </button>
              <button
                className={`stui-action-menu-item${activeFilterCount === 0 && sortOrder.length === 0 ? ' disabled' : ''}`}
                disabled={activeFilterCount === 0 && sortOrder.length === 0}
                onClick={() => { clearFiltersAndSort(); setExportMenuOpen(false); }}
              >
                <FilterX size={16} /><span>Clear Filters &amp; Sort</span>
              </button>
            </div>
          )}
        </div>
        </div>{/* stui-module-header-actions */}
      </div>{/* stui-module-header */}

      <div className="stui-tab-body">

      {/* ===== Section 1: Report Configuration ===== */}
      {config.parameters && config.parameters.length > 0 && (
        <div className="stui-step-panel">
          <div
            className="stui-step-header clickable"
            onClick={() => setSec1Collapsed(c => !c)}
          >
            <span className="stui-step-number">1</span>
            <h3>Report Configuration</h3>
            <span className="stui-step-chevron">
              {sec1Collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
            </span>
          </div>
          {!sec1Collapsed && <p className="stui-step-desc">Select filter values to narrow report results.</p>}
          {!sec1Collapsed && <div>
            <ReportParameterTable
              params={config.parameters}
              values={parameters}
              onChange={(field, sels) => updateParameter(field, sels)}
              reportId={reportSlug}
              paramOptions={paramOptions}
              sortLookup={Object.fromEntries(sortOrder.map((s, i) => [s.field, i + 1]))}
            />
            {/* Options row: Sort | Group By | QR */}
            <div className="stui-options-row">
              {/* Sort By comboboxes */}
              {[
                { label: 'Sort by', idx: 0 },
                { label: 'Then',    idx: 1 },
                { label: 'Then',    idx: 2 },
                { label: 'Then',    idx: 3 },
              ].map(({ label, idx }) => (
                <div key={idx} className="stui-sort-slot">
                  <span className="stui-label">{label}:</span>
                  <select
                    className="stui-select"
                    value={sortSlots[idx].field}
                    onChange={e => {
                      const updated = sortSlots.map((s, i) => i === idx ? { ...s, field: e.target.value } : s);
                      setSortSlots(updated);
                    }}
                  >
                    <option value="">—</option>
                    {(config?.columns || []).map(col => (
                      <option key={col.field} value={col.field}>{col.label || col.field}</option>
                    ))}
                  </select>
                  {sortSlots[idx].field && (
                    <select
                      className="stui-select"
                      value={sortSlots[idx].dir}
                      onChange={e => {
                        const updated = sortSlots.map((s, i) => i === idx ? { ...s, dir: e.target.value } : s);
                        setSortSlots(updated);
                      }}
                    >
                      <option value="asc">Asc</option>
                      <option value="desc">Desc</option>
                    </select>
                  )}
                </div>
              ))}
              {/* Group By */}
              {(config?.parameters?.length > 0) && (
                <div className="stui-sort-slot">
                  <span className="stui-label">Group By:</span>
                  <select
                      className="stui-select"
                      value={exportGroupBy || ''}
                      onChange={e => setExportGroupBy(e.target.value || null)}
                      disabled={!!FIXED_GROUP_BY[reportSlug]}
                      title={FIXED_GROUP_BY[reportSlug] ? "Grouping is fixed for this report and cannot be changed" : "Group the report preview and export output by unique values of this column"}
                    >
                      <option value="">—</option>
                      {(config?.parameters || []).map(col => <option key={col.field} value={col.field}>{col.label}</option>)}
                    </select>
                </div>
              )}
              {/* QR Code checkbox */}
              <label className="stui-checkbox">
                <input type="checkbox" className="stui-checkbox-input" checked={showQrCode} onChange={e => setShowQrCode(e.target.checked)} />
                Show QR Code in report header
              </label>
            </div>
            {/* Stats row */}
            <div className="stui-stat-row">
              <div className="stui-stat-group">
                {[
                  { label: 'Rows',      value: hasRun ? totalCount.toLocaleString() : '—' },
                  { label: 'Pages',     value: hasRun ? totalPages : '—' },
                  { label: 'Rows/Page', value: hasRun ? pageSize : '—' },
                  { label: 'Layout',    value: config?.layout?.defaultPageSize || 'tabloid' },
                  { label: 'Time',      value: hasRun && executionMs != null ? `${executionMs}ms` : '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="stui-stat-item">
                    <span className="stui-stat-label">{label}</span>
                    <span className="stui-stat-value">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>}
        </div>
      )}

      {/* ===== Section 2: Report Preview ===== */}
      <div className="stui-step-panel stui-step-panel-no-overflow">
        <div
          className="stui-step-header clickable"
          onClick={() => setSec2Collapsed(c => !c)}
        >
          <span className="stui-step-number">2</span>
          <h3>Report Preview</h3>
          <button
            className="stui-btn stui-btn-primary stui-step-action"
            onClick={e => { e.stopPropagation(); runReport(); }}
            disabled={!canRun}
            title={activeFilterCount === 0 ? 'Select at least one parameter before running' : undefined}
          >
            <Play size={16} /> {runLabel}
          </button>
          <span className="stui-step-chevron">
            {sec2Collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </span>
        </div>

      {!sec2Collapsed && <>
      <p className="stui-step-desc">Preview matches the PDF export layout.</p>
      {/* Pre-run placeholder — no scale wrapper, no reflow risk */}
      {!hasRun && !loading && (
        <div className="stui-report-placeholder">
          <p>Set parameters and click <strong>Run Report</strong> to view data.</p>
        </div>
      )}

      {/* Scaled preview — only rendered after first run */}
      {hasRun && (
      <>
      <div ref={previewContainerRef} className="stui-rpt-preview-container">
      <div
        ref={previewContentRef}
        className="stui-rpt-preview-scale"
        style={{
          transform: `scale(${previewScale})`,
          width: '1632px', // TABLOID landscape: 1224pt × (96/72) px/pt
          marginBottom: previewScale < 1 ? `${(previewScale - 1) * (previewContentRef.current?.offsetHeight ?? 0)}px` : 0
        }}
      >

      {/* Report Page Header — mirrors PDF output exactly */}
      <div className="stui-rpt-preview-header">
        {/* Left: WM logo + QR code */}
        <div className="stui-rpt-preview-logo-block">
          <img src={wmLogo} alt="Wunderlich-Malec" style={{ height: '96px', width: 'auto' }} />
          {showQrCode && qrDataUrl && (
            <div className="stui-rpt-preview-qr-block">
              <img
                src={qrDataUrl}
                alt="QR Code"
                title="Scan to open this report in DAWN"
                style={{ height: '86px', width: '86px', imageRendering: 'pixelated' }}
              />
              <span className="stui-rpt-preview-qr-label">Scan to open in DAWN</span>
            </div>
          )}
        </div>

        {/* Spacer to push parameters right */}
        <div className="stui-rpt-preview-spacer" />

        {/* Center: Report Title — absolutely centered over full header width */}
        <div className="stui-rpt-preview-title-block">
          <div className="stui-rpt-preview-title" style={{ color: config?.branding?.titleColor || '#1f497d' }}>
            {reportDisplayName}
          </div>
          {groupedPages?.[currentPage - 1]?.subtitle && (
            <div className="stui-rpt-preview-subtitle" style={{ color: config?.branding?.titleColor || '#1f497d' }}>
              {groupedPages[currentPage - 1].subtitle}
            </div>
          )}
          {hasRun && (
            <div className="stui-rpt-preview-record-count">
              {groupedPages
                ? `${(groupedPages[currentPage - 1]?.groupRowCount || 0).toLocaleString()} Records`
                : `${sortedData.length.toLocaleString()} Records`}
            </div>
          )}
        </div>

        {/* Right: Selected parameters — fixed 3-col × 2-row grid (6 positions).
            Params anchor to position 1 (bottom-right) and fill toward position 6 (top-left).
            Values frozen at last run time (committedParameters), not live selections. */}
        {(() => {
          const allParams = config.parameters || [];
          const params = isIndexReport
            ? allParams.filter(p => p.field !== 'Index_Sheet')
            : allParams;

          // Build 6-slot array: slots[0]=pos6 .. slots[5]=pos1
          // With n params: last n slots are filled, earlier slots are null (empty)
          const NUM_SLOTS = 6;
          const slots = Array(NUM_SLOTS).fill(null);
          params.forEach((p, i) => { slots[NUM_SLOTS - params.length + i] = p; });

          // Column c: top=slots[c*2], bottom=slots[c*2+1]
          const colWidths = [0, 1, 2].map(c =>
            Math.max(
              slots[c * 2] ? (paramHeaderWidths[slots[c * 2].field] || 80) : 0,
              slots[c * 2 + 1] ? (paramHeaderWidths[slots[c * 2 + 1].field] || 80) : 0,
              80
            )
          );

          const renderSlot = (slotIdx, colWidth) => {
            const param = slots[slotIdx];
            if (!param) return <div key={`empty-${slotIdx}`} className="stui-rpt-preview-param-slot" style={{ width: `${colWidth}px` }} />;
            const selsList = committedParameters[param.field] || [];
            return (
              <div key={param.field} className="stui-rpt-preview-param-slot" style={{ width: `${colWidth}px` }}>
                <div className="stui-rpt-preview-param-label">{param.label}</div>
                {selsList.length === 0 ? (
                  <div className="stui-rpt-preview-param-none">None Selected</div>
                ) : (
                  <>
                    {(selsList.length > 4 ? selsList.slice(0, 3) : selsList).map((sel, i) => (
                      <div key={i} title={sel.fullDisplay || sel.display} className="stui-rpt-preview-param-value">
                        {sel.fullDisplay || sel.display}
                      </div>
                    ))}
                    {selsList.length > 4 && (
                      <div className="stui-rpt-preview-param-overflow">Greater than 4 Selected</div>
                    )}
                  </>
                )}
              </div>
            );
          };

          return (
            <div className="stui-rpt-preview-params">
              <div className="stui-rpt-preview-params-row">
                {[0, 2, 4].map((si, ci) => renderSlot(si, colWidths[ci]))}
              </div>
              <div className="stui-rpt-preview-params-row">
                {[1, 3, 5].map((si, ci) => renderSlot(si, colWidths[ci]))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Data Table */}
      {hasRun && (
        <div className="stui-rpt-preview-table-wrap">
          <table className="stui-rpt-preview-table">
            <thead className="stui-rpt-preview-thead">
              <tr>
                {currentPageHeaderColumns.map(col => {
                  const sortInfo = sortLookup[col.field];
                  return (
                    <th
                      key={col.field}
                      title={col.label}
                      className="stui-rpt-preview-th"
                      style={{
                        width: previewColWidths[col.field] || `${col.pixelWidth || col.width * 10 || 80}px`,
                        textAlign: col.align || 'left',
                      }}
                    >
                      {(() => {
                        const [hLine1, hLine2] = splitHeaderLabel(col.label);
                        return (
                          <>
                            <span className="stui-rpt-preview-th-line">{hLine1}</span>
                            <span className="stui-rpt-preview-th-line">{hLine2}</span>
                            <span className="stui-rpt-preview-th-sort">
                              {sortInfo
                                ? `${sortInfo.priority} ${sortInfo.direction === 'asc' ? 'asc' : 'dsc'}`
                                : '\u00a0'}
                            </span>
                          </>
                        );
                      })()}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={displayColumns.length} className="stui-rpt-preview-empty-row">
                    No data returned
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, idx) => (
                  <tr
                    key={row.id || idx}
                    style={{
                      backgroundColor: config.layout?.zebraStriping && idx % 2 === 1
                        ? 'var(--color-gray-100)' : 'var(--color-bg-white)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-teal-50)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor =
                      config.layout?.zebraStriping && idx % 2 === 1
                        ? 'var(--color-gray-100)' : 'var(--color-bg-white)'}
                  >
                    {displayColumns.map(col => {
                      const camelField = toCamelCase(col.field);
                      const displayKey = `${camelField}_Display`;
                      const displayValue = row[displayKey] != null ? row[displayKey] : row[camelField];

                      const isCellWrappable = col.field === 'Note' || col.field === 'Comment';
                      return (
                        <td
                          key={col.field}
                          className={`stui-rpt-preview-td${isCellWrappable ? ' wrap' : ''}`}
                          style={{ textAlign: isCellWrappable ? 'left' : (col.align || 'left') }}
                        >
                          {displayValue != null ? String(displayValue) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Report Preview Footer — mirrors PDF footer, inside previewContentRef so width matches table */}
      {hasRun && (
        <div className="stui-rpt-preview-footer">
          <span>{new Date().toLocaleString()}</span>
          {(clientIconUrl || clientName) && (
            <span className="stui-rpt-preview-footer-org">
              {clientIconUrl && <img src={clientIconUrl} alt="Client" style={{ height: '16px', width: 'auto', objectFit: 'contain' }} />}
              {clientName} Confidential
            </span>
          )}
          {dawnVersion && <span>Dawn - {dawnVersion}</span>}
          <span className="stui-rpt-preview-footer-wm">
            <img src={wmLogoCb} alt="Wunderlich-Malec" style={{ height: '16px', width: 'auto' }} />
            Wunderlich-Malec Confidential
          </span>
          <span>{hasRun ? `Page ${currentPage} of ${totalPages}` : ''}</span>
        </div>
      )}

      </div>{/* close previewContentRef */}
      </div>{/* close previewContainerRef */}

      {/* Paginator — outside scaled area so controls stay full size */}
      {hasRun && (
        <ReportPaginator
          currentPage={currentPage}
          totalPages={totalPages}
          totalRows={sortedData.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      )}

      </>
      )}{/* close hasRun */}
      </>}{/* close !sec2Collapsed */}

      </div>{/* close Section 2 stui-step-panel */}

      </div>{/* stui-tab-body */}
    </div>
  );
};

export default ReportViewerPage;
